/**
 * Asset library (§7.2), shared by the owner and renter entry points.
 *
 * §7.6: "nothing has been generated" and "everything is filtered out" are
 * different screens. Collapsing them makes a working library look broken, so the
 * component asks the server whether anything exists at all, independently of the
 * current filters.
 */

import { useState } from 'react';
import { EMPTY_STATES } from '@pm/shared';
import { useApi, useMutation } from '../../hooks/useApi';
import { useBotOutlet } from '../botOutlet';
import type { Asset, Page } from '../../api/types';
import { Gate } from '../../components/Gate';
import { Card, Field, Loading, Badge } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { localDateTime } from '../../lib/format';
import { routes } from '../../api/routes';

export function AssetLibrary() {
  const { bot, caps } = useBotOutlet();
  const [kind, setKind] = useState('');
  const [source, setSource] = useState('');

  // Existence probe: one row, no filters. Cheap, and it is the only way to tell
  // "empty" from "filtered to nothing" without trusting the client's own filters.
  const any = useApi<Page<Asset>>(routes.content.assets(bot.id), { query: { limit: 1 } });
  const list = useApi<Page<Asset>>(routes.content.assets(bot.id), {
    query: { limit: 50, kind, source },
  });

  const [text, setText] = useState('');
  const upload = useMutation<Record<string, unknown>, Asset>(routes.content.assets(bot.id));

  const filtered = kind !== '' || source !== '';
  const assets = list.data?.data ?? [];
  const anyAtAll = (any.data?.data.length ?? 0) > 0;

  return (
    <div className="library">
      <Card
        title="Library"
        actions={
          <div className="filters-inline">
            <Field id="lib-kind" label="Kind">
              <select
                id="lib-kind"
                className="select"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                <option value="">Any</option>
                <option value="text">Text</option>
                <option value="image">Image</option>
              </select>
            </Field>
            <Field id="lib-source" label="Source">
              <select
                id="lib-source"
                className="select"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <option value="">Any</option>
                <option value="generated">Generated</option>
                <option value="uploaded">Uploaded</option>
              </select>
            </Field>
          </div>
        }
      >
        {list.initialLoading ? <Loading label="Loading the library" /> : null}
        {list.problem ? <ErrorView problem={list.problem} onRetry={list.reload} /> : null}

        {!list.initialLoading && assets.length === 0 ? (
          anyAtAll && filtered ? (
            <EmptyState
              heading={EMPTY_STATES.libraryAllFiltered.heading}
              body={EMPTY_STATES.libraryAllFiltered.body}
              cta={{
                label: 'Clear filters',
                onClick: () => {
                  setKind('');
                  setSource('');
                },
              }}
            />
          ) : (
            <EmptyState
              heading={EMPTY_STATES.libraryNoAssets.heading}
              body={EMPTY_STATES.libraryNoAssets.body}
            />
          )
        ) : (
          <ul className="asset-list">
            {assets.map((a) => (
              <li key={a.id} className="asset">
                <div>
                  <p>
                    <Badge tone="info">{a.kind}</Badge>{' '}
                    <Badge tone={a.provenance.source === 'generated' ? 'neutral' : 'ok'}>
                      {a.provenance.source}
                    </Badge>
                    {a.provenance.model ? <span className="muted"> {a.provenance.model}</span> : null}
                  </p>
                  {a.kind === 'text' ? (
                    <p className="preview">{a.body}</p>
                  ) : a.url ? (
                    <img className="asset-thumb" src={a.url} alt="" />
                  ) : (
                    <p className="muted">{a.mime_type}</p>
                  )}
                  <p className="muted">{localDateTime(a.created_at)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Gate caps={caps} capability="upload_asset">
        {(disabled) => (
          <Card title="Add an asset">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if ((await upload.run({ kind: 'text', body: text, rights_attested: true })) !== null) {
                  setText('');
                  list.reload();
                  any.reload();
                }
              }}
            >
              <fieldset disabled={disabled}>
                <Field
                  id="lib-new"
                  label="Text asset"
                  hint="By adding it you confirm you have the rights to it."
                >
                  <textarea
                    id="lib-new"
                    className="textarea"
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </Field>
                {upload.problem ? <InlineError problem={upload.problem} /> : null}
                <button type="submit" className="btn btn-primary" disabled={disabled || !text.trim()}>
                  Add to the library
                </button>
              </fieldset>
            </form>
          </Card>
        )}
      </Gate>
    </div>
  );
}
